"use client"

import { useEffect, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { ApiClientError } from "@/lib/ApiClientError"
import { ResendVerificationApi, ResendVerificationResponse } from "@/lib/resend-verification/resend-verification.api"

/**
 * @param enabled  Only probe raauth's rate-limit status when this is true — pass the
 *   modal's `isOpen`. Otherwise a closed VerifyEmail (rendered on every page, and
 *   twice on /profile) would fire an authenticated `GET /api/users/resend-verification`
 *   on mount, which 401-storms whenever the session is missing/stale.
 */
export function useVerifyEmailMutation(email: string | null | undefined, enabled: boolean = false) {
   const [blockUntil, setBlockUntil] = useState<number | null>(null)
   const [hasSent, setHasSent] = useState(false)
   const [, setTick] = useState(0)
   // Starts true (Button disabled) until we check the raauth status
   const [isCheckingStatus, setIsCheckingStatus] = useState(true)

   // Restore the block from raauth's actual rate-limit counter after a page
   // refresh — the 60s post-send cooldown is a client-only nicety and doesn't
   // need this, but the real 24h/3-attempt block otherwise looks cleared.
   useEffect(() => {
      if (!email || !enabled) {
         setIsCheckingStatus(false)
         return
      }
      let cancelled = false
      ResendVerificationApi.status()
         .then(({ unlocksAt }) => {
            if (cancelled || !unlocksAt) return
            const ms = new Date(unlocksAt).getTime()
            if (ms > Date.now()) {
               setBlockUntil(ms)
               setHasSent(true)
            }
         })
         .catch(() => {})
         .finally(() => {
            if (!cancelled) setIsCheckingStatus(false)
         })
      return () => {
         cancelled = true
      }
   }, [email, enabled])

   useEffect(() => {
      if (!blockUntil) return
      const id = setInterval(() => {
         if (Date.now() >= blockUntil) setBlockUntil(null)
         else setTick((t) => t + 1)
      }, 1000)
      return () => clearInterval(id)
   }, [blockUntil])

   const mutation = useMutation<ResendVerificationResponse, unknown, void>({
      mutationKey: ["verifyEmail", email],
      meta: { suppressGlobalToast: true },
      mutationFn: async () => {
         return ResendVerificationApi.resend()
      },
      onSuccess: (data) => {
         toast.success("Email Sent Successfully")
         setHasSent(true)
         if (data.cooldownUntil) setBlockUntil(new Date(data.cooldownUntil).getTime())
      },
      onError: (error) => {
         if (error instanceof ApiClientError) {
            if (error.status === 429) {
               const payload = error.payload as { unlocksAt?: string } | undefined
               if (payload?.unlocksAt) setBlockUntil(new Date(payload.unlocksAt).getTime())
               toast.error("You have sent too many verification requests. Please wait 24h and try again.")
            } else {
               toast.error("System error. Please try again later.")
            }
         } else {
            toast.error("An unexpected error occurred. Please try again.")
         }
      },
   })

   const isBlocked = !!blockUntil && Date.now() < blockUntil

   function getButtonText(defaultText: string): string {
      if (mutation.isPending) return "Sending link..."
      if (!isBlocked) return defaultText
      const ms = blockUntil! - Date.now()
      const s = Math.ceil(ms / 1000)
      if (s <= 60) return `Resend available in ${s}s`
      const h = Math.floor(ms / 3_600_000)
      const m = Math.floor((ms % 3_600_000) / 60_000)
      const sec = Math.floor((ms % 60_000) / 1000)
      if (h > 0) return `Try again in ${h}h ${m}m`
      if (m > 0) return `Try again in ${m}m ${sec}s`
      return `Try again in ${sec}s`
   }

   return {
      trigger: () => {
         if (!email || mutation.isPending || isBlocked || isCheckingStatus) return
         mutation.mutate()
      },
      isPending: mutation.isPending,
      isDisabled: mutation.isPending || isBlocked || isCheckingStatus,
      hasSent,
      getButtonText,
   }
}
